/**
 * Round 91 — "Why This Fits You" partnership reasoning block.
 *
 * Small, embeddable component that any recommendation modal can slot
 * into its output. Reads the current user's Life Blueprint +
 * Business Memory + Learning Signals via POST
 * /api/paygbot/why-this-fits-you and renders a warm 2-5 sentence
 * explanation.
 *
 * Props
 * -----
 *   department              — 'business_builder' | 'money' | 'offer'
 *                             | 'content' | 'growth' | 'operations'
 *   taskType                — optional string
 *   recommendationSummary   — short human phrase describing the
 *                             recommendation. If omitted, a general
 *                             department-level explanation is used.
 *   businessCtx             — optional dict passed through as-is.
 *   className               — style overrides.
 */
import React, { useEffect, useRef, useState } from "react";
import api from "@/utils/api";
import { Sparkles, ThumbsUp, ThumbsDown, Check } from "lucide-react";

export default function WhyThisFitsYouBlock({
  department,
  taskType,
  recommendationSummary,
  recommendationId,
  businessCtx,
  className = "",
}) {
  const [block, setBlock] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Feedback state — locked once submitted to prevent dupes.
  const [feedbackSent, setFeedbackSent] = useState(null);   // "approved" | "rejected" | null
  const [showNoteBox, setShowNoteBox] = useState(false);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Ref-based mutex — bullet-proof against synchronous multi-clicks
  // (React state hasn't flushed yet). Round 92 hardening.
  const sentLockRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await api.post("/paygbot/why-this-fits-you", {
          department: department || null,
          task_type: taskType || null,
          recommendation_summary: recommendationSummary || null,
          business_ctx: businessCtx || {},
        });
        if (!cancelled) setBlock(resp.data);
      } catch (err) {
        if (!cancelled) {
          setError(err);
          console.warn("[WhyThisFitsYouBlock] fetch failed", err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [department, taskType, recommendationSummary, businessCtx]);

  async function _submitFeedback(kind, noteText) {
    if (sentLockRef.current) return;             // synchronous mutex
    if (feedbackSent || submitting) return;       // React-state dedupe
    sentLockRef.current = true;
    const recId = recommendationId || `rec-adhoc-${Date.now()}`;
    setSubmitting(true);
    try {
      await api.post(`/paygbot/recommendation/${recId}/feedback`, {
        feedback: kind === "approved" ? "approved" : "rejected",
        department: department || null,
        task_type: taskType || null,
        note: (noteText || "").slice(0, 500) || null,
      });
      setFeedbackSent(kind);
    } catch (err) {
      console.warn("[WhyThisFitsYouBlock] feedback failed", err);
      // Silent failure — mark as sent locally so the buttons dedupe
      // AND the modal keeps working. Round 92 rule: never block flow.
      setFeedbackSent(kind);
    } finally {
      setSubmitting(false);
      setShowNoteBox(false);
    }
  }

  // Silent failure — never break the modal.
  if (loading || error || !block) return null;
  if (!block.sentences || block.sentences.length === 0) return null;

  return (
    <div
      className={`rounded-xl border border-emerald-500/20 bg-gradient-to-br from-emerald-500/8 to-teal-500/5 p-4 ${className}`}
      data-testid="why-this-fits-you-block"
    >
      <div className="flex items-center gap-2 mb-2">
        <Sparkles size={16} className="text-emerald-400" />
        <h4
          className="text-white font-semibold text-sm"
          data-testid="why-fits-title"
        >
          {block.title || "Why This Fits You"}
        </h4>
      </div>

      {block.intro && (
        <p className="text-white/70 text-xs mb-2">{block.intro}</p>
      )}

      {Array.isArray(block.bullets) && block.bullets.length > 0 && (
        <ul className="mb-3 space-y-1">
          {block.bullets.map((b, i) => (
            <li
              key={i}
              className="flex items-start gap-2 text-white/80 text-xs"
            >
              <span className="mt-1 h-1 w-1 rounded-full bg-emerald-400 shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}

      <div
        className="text-white/85 text-sm leading-relaxed space-y-1.5"
        data-testid="why-fits-sentences"
      >
        {block.sentences.map((s, i) => (
          <p key={i}>{s}</p>
        ))}
      </div>

      {/* Round 92 — feedback controls. These evaluate whether the
          REASONING fits. They do NOT approve or execute the underlying
          recommendation itself. */}
      <div
        className="mt-3 pt-3 border-t border-emerald-500/10"
        data-testid="why-fits-feedback"
      >
        {!feedbackSent && !showNoteBox && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => _submitFeedback("approved")}
              disabled={submitting}
              className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 disabled:opacity-40"
              data-testid="why-fits-thumbs-up"
            >
              <ThumbsUp size={12} /> This fits — keep going
            </button>
            <button
              type="button"
              onClick={() => setShowNoteBox(true)}
              disabled={submitting}
              className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs bg-white/5 hover:bg-white/10 text-white/70 disabled:opacity-40"
              data-testid="why-fits-thumbs-down"
            >
              <ThumbsDown size={12} /> This misses something
            </button>
          </div>
        )}

        {!feedbackSent && showNoteBox && (
          <div className="space-y-2" data-testid="why-fits-note-panel">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 500))}
              rows={2}
              placeholder="Optional — what did I miss? (one line is fine)"
              className="w-full rounded-lg border border-white/10 bg-slate-900/60 px-3 py-2 text-xs text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-emerald-400/30 resize-none"
              data-testid="why-fits-note-input"
              disabled={submitting}
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => _submitFeedback("rejected", note)}
                disabled={submitting}
                className="px-3 py-1 rounded-full text-xs bg-emerald-500/80 hover:bg-emerald-400 text-white disabled:opacity-50"
                data-testid="why-fits-submit-miss"
              >
                {submitting ? "Sending…" : "Send"}
              </button>
              <button
                type="button"
                onClick={() => { setShowNoteBox(false); setNote(""); }}
                disabled={submitting}
                className="text-white/50 hover:text-white/80 text-xs"
                data-testid="why-fits-cancel-note"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {feedbackSent === "approved" && (
          <p
            className="flex items-center gap-1.5 text-emerald-300 text-xs"
            data-testid="why-fits-confirm-approved"
          >
            <Check size={12} /> Thank you — I&apos;ll keep this direction.
          </p>
        )}
        {feedbackSent === "rejected" && (
          <p
            className="flex items-center gap-1.5 text-emerald-300 text-xs"
            data-testid="why-fits-confirm-rejected"
          >
            <Check size={12} /> Thank you. I&apos;ll use this to improve the next recommendation.
          </p>
        )}
      </div>
    </div>
  );
}
